import React, { useState, Fragment } from "react";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import {
  Button,
  Toolbar,
  Box,
  Backdrop,
  CircularProgress,
  Snackbar,
  Alert,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { logout, getBillingPortalUrl } from "./services/authService";
import {
  BILLING_PORTAL_ENABLED,
  PREMIUM_HEADER_CTA,
  PREMIUM_PURCHASING_BUTTON,
} from "./billing";
import { INQUIRY_FORM_URL, SiteLink } from "./links";
import SiteLinkItem from "./siteLink";
import {
  NOTSIGNEDIN,
  VERYFING,
  PURCHASING,
  PREMIUM,
  SignInStatus,
} from "./states";

// ヘッダーのナビゲーション項目の唯一の定義。
// ドロワー（モバイル）と横並び（デスクトップ）の両方がこれを参照する。
const NAV_ITEMS: SiteLink[] = [
  { label: "首页", to: "/" },
  { label: "咨询", href: INQUIRY_FORM_URL },
  { label: "价格方案", to: "/pricing" },
];

interface DrawerMenuProps {
  setDrawerOpened: (opened: boolean) => void;
}

const DrawerMenu = (props: DrawerMenuProps) => {
  return (
    <Box
      sx={{ width: 250, mt: 4 }}
      role="presentation"
      onClick={() => props.setDrawerOpened(false)}
    >
      <List>
        {NAV_ITEMS.map((item) => (
          <ListItem key={item.label} disablePadding>
            {item.to !== undefined ? (
              <ListItemButton component={RouterLink} to={item.to}>
                <ListItemText primary={item.label} />
              </ListItemButton>
            ) : (
              <ListItemButton href={item.href} target="_blank" rel="noreferrer">
                <ListItemText primary={item.label} />
              </ListItemButton>
            )}
          </ListItem>
        ))}
      </List>
    </Box>
  );
};

interface Props {
  signInStatus: SignInStatus;
  ticketPremium: boolean;
  onLogout: () => void;
}

const Header = (props: Props) => {
  const [drawerOpened, setDrawerOpened] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [showSnack, setShowSnack] = useState(false);
  const [snackMessage, setSnackMessage] = useState<string>();
  const navigate = useNavigate();
  const theme = useTheme();
  // 幅が足りない画面ではナビゲーションをドロワーにたたみ、
  // 右側のボタンも小さくして横あふれを防ぐ
  const isCompact = useMediaQuery(theme.breakpoints.down("md"));
  const buttonSize = isCompact ? "small" : "large";
  const onClickBillingPortal = async () => {
    if (!BILLING_PORTAL_ENABLED) {
      setSnackMessage("当前方案无需付款管理");
      setShowSnack(true);
      return;
    }

    setLoading(true);

    const result = await getBillingPortalUrl(window.location.href, "zh");
    if (result.ok && result.url) {
      window.location.assign(result.url);
    } else {
      setLoading(false);
      setSnackMessage("无法打开付款管理页面");
      setShowSnack(true);
    }
  };

  const handleLogout = async () => {
    await logout();
    props.onLogout();
    navigate("/");
    setSnackMessage("已注销");
    setShowSnack(true);
  };

  return (
    <Toolbar
      sx={{
        borderBottom: 1,
        borderColor: "divider",
        gap: 1,
      }}
    >
      {isCompact && (
        <Fragment>
          <MenuIcon
            onClick={() => setDrawerOpened(true)}
            style={{ cursor: "pointer", flexShrink: 0 }}
          />
          <Drawer
            anchor={"left"}
            open={drawerOpened}
            onClose={() => setDrawerOpened(false)}
          >
            <DrawerMenu setDrawerOpened={setDrawerOpened} />
          </Drawer>
        </Fragment>
      )}
      {!isCompact &&
        NAV_ITEMS.map((item) => (
          <SiteLinkItem key={item.label} link={item} />
        ))}
      <Box sx={{ flex: 1 }}></Box>
      {BILLING_PORTAL_ENABLED &&
        props.signInStatus === PREMIUM &&
        !props.ticketPremium && (
          <Button
            variant="text"
            size={buttonSize}
            onClick={onClickBillingPortal}
          >
            付款管理
          </Button>
        )}
      {[VERYFING, PURCHASING, PREMIUM].includes(props.signInStatus) && (
        <Button variant="text" size={buttonSize} onClick={handleLogout}>
          注销
        </Button>
      )}
      {props.signInStatus === NOTSIGNEDIN && (
        <Button
          variant="text"
          size={buttonSize}
          to="/login"
          component={RouterLink}
        >
          登录
        </Button>
      )}
      {props.signInStatus === NOTSIGNEDIN && (
        <Button
          variant="contained"
          size={buttonSize}
          to="/pricing"
          component={RouterLink}
        >
          {PREMIUM_HEADER_CTA}
        </Button>
      )}
      {props.signInStatus === VERYFING && (
        <Button
          variant="contained"
          size={buttonSize}
          to="/signup"
          component={RouterLink}
        >
          重新发送确认邮件
        </Button>
      )}
      {props.signInStatus === PURCHASING && (
        <Button
          variant="contained"
          size={buttonSize}
          to="/pricing"
          component={RouterLink}
        >
          {PREMIUM_PURCHASING_BUTTON}
        </Button>
      )}
      {isLoading && (
        <Fragment>
          <Backdrop
            sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
            open={isLoading}
          >
            <CircularProgress color="inherit" />
          </Backdrop>
        </Fragment>
      )}
      <Snackbar
        open={showSnack}
        autoHideDuration={6000}
        onClose={() => setShowSnack(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setShowSnack(false)}
          severity="info"
          sx={{ width: "100%" }}
        >
          {snackMessage}
        </Alert>
      </Snackbar>
    </Toolbar>
  );
};

export default Header;
